import React, { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Forced password reset on first login (Beta-1 plan P4-11). `hugoCreateOrgUser` stamps the custom
 * claim `hugoForcePasswordReset: true` on accounts it creates with a temporary password; the app
 * blocks until the member sets their own password, then records `passwordResetDone` in their
 * preferences (the claim itself can only be cleared server-side and is ignored once this is set).
 */
export default function ForcePasswordResetDialog() {
  const { user } = useAuth();
  const [needed, setNeeded] = useState(false);
  const [current, setCurrent] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) { setNeeded(false); return; }
      try {
        const token = await user.getIdTokenResult();
        if (!token.claims?.hugoForcePasswordReset) { if (!cancelled) setNeeded(false); return; }
        const prefs = await getDoc(doc(db, 'Hugo_UserPreferences', user.uid));
        if (!cancelled) setNeeded(!(prefs.exists() && prefs.data()?.passwordResetDone));
      } catch {
        if (!cancelled) setNeeded(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (!user || !needed) return null;

  const submit = async () => {
    setError('');
    if (pw.length < 8) { setError('Use at least 8 characters.'); return; }
    if (pw !== pw2) { setError('The two passwords do not match.'); return; }
    if (pw === current) { setError('Choose a password different from the temporary one.'); return; }
    setBusy(true);
    try {
      if (current) {
        await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, current));
      }
      await updatePassword(user, pw);
      await setDoc(doc(db, 'Hugo_UserPreferences', user.uid), { passwordResetDone: true, passwordResetAt: serverTimestamp() }, { merge: true });
      setNeeded(false);
    } catch (err) {
      if (err?.code === 'auth/requires-recent-login') setError('Please enter the temporary password you signed in with, then try again.');
      else if (err?.code === 'auth/wrong-password' || err?.code === 'auth/invalid-credential') setError('That temporary password is not right.');
      else setError(err?.message || 'Could not change the password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open disableEscapeKeyDown maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LockResetIcon color="primary" /> Set your password
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You signed in with a temporary password. Choose your own before continuing — it is not shared with anyone.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField margin="dense" type="password" label="Temporary password" fullWidth value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" helperText="The one from your welcome email (needed to confirm it is you)" />
        <TextField margin="dense" type="password" label="New password" fullWidth value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" />
        <TextField margin="dense" type="password" label="Repeat new password" fullWidth value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={submit} disabled={busy || !pw || !pw2}>{busy ? 'Saving…' : 'Save password'}</Button>
      </DialogActions>
    </Dialog>
  );
}
